import { type LoaderFunction, replace } from 'react-router';

import { type PrivacyPolicySignStatusDto } from '@/api/generated/models/privacy-policy-sign-status-dto';
import { type RoleTypeDto } from '@/api/generated/models/role-type-dto';
import { type UserPermissionDto } from '@/api/generated/models/user-permission-dto';
import { redirectOnAuthFailure } from '@/api/util/redirectOnAuthFailure.util.ts';
import { readCurrentUser } from '@/contexts/userContext/currentUserSnapshot.util.ts';
import { hasAccessByPermissionCheck, isAuthorized } from '@/layouts/private/authorization.ts';
import { RouteEnum } from '@/layouts/routes/Route.model.ts';
import { ROUTES_PERMISSIONS, ROUTES_ROLES } from '@/layouts/routes/routesRoles.model.ts';
import { PATHS_NOT_REQUIRING_AUTHORIZATION } from '@/model/routes.consts.ts';
import { isPrivacyPolicySigningRequired } from '@/views/signPrivacyPolicy/util/isPrivacyPolicySigningRequired';

/**
 * Routes whose entry never consults the guard (OCD-309): the public screens
 * plus the two remediation screens themselves — guarding them would loop,
 * since they are where a blocked user is sent to fix their state.
 */
export const ROUTES_EXEMPT_FROM_GUARD: string[] = [
  ...PATHS_NOT_REQUIRING_AUTHORIZATION,
  RouteEnum.USERS_INITIALIZE,
  RouteEnum.USERS_SIGN_PRIVACY_POLICY,
];

export type RouteAccess = Response | null;

/**
 * Pure guard decision for entering a route (OCD-309): the single mechanism
 * behind every route entry, direct URL included. Order matters: an
 * uninitialized user must reach the initialize screen even before approval,
 * and a role-less user lands on waiting-for-approval as before. Redirects
 * replace the history entry, like the <Navigate replace> they stand in for.
 */
export const resolveRouteAccess = (
  path: RouteEnum,
  user: {
    initializationDone: boolean;
    permissions: UserPermissionDto[];
    privacyPolicyStatus: PrivacyPolicySignStatusDto;
    role?: RoleTypeDto;
  },
): RouteAccess => {
  if (!user.initializationDone) {
    return replace(RouteEnum.USERS_INITIALIZE);
  }

  if (user.role === undefined) {
    return replace(RouteEnum.WAITING_FOR_APPROVAL);
  }

  if (isPrivacyPolicySigningRequired(user.privacyPolicyStatus)) {
    return replace(RouteEnum.USERS_SIGN_PRIVACY_POLICY);
  }

  const hasAccessByRole = isAuthorized(ROUTES_ROLES[path], user.role);
  const hasAccessByPermission = hasAccessByPermissionCheck(
    ROUTES_PERMISSIONS[path],
    user.permissions,
  );

  if (!hasAccessByRole || !hasAccessByPermission) {
    return replace(RouteEnum.FORBIDDEN);
  }

  return null;
};

/**
 * Loader behind every guarded route (OCD-309): replaces the render-time
 * RouteGuard, the UserContextVerifier privacy redirect and the
 * useOcdHistory push-time redirects with one entry check that also covers
 * direct URL entry.
 */
export const createRouteGuardLoader =
  (path: RouteEnum): LoaderFunction =>
  async () => {
    if (ROUTES_EXEMPT_FROM_GUARD.includes(path)) {
      return null;
    }

    try {
      const user = await readCurrentUser();
      return resolveRouteAccess(path, user);
    } catch (error) {
      if (!redirectOnAuthFailure(error)) {
        throw error;
      }
      // A window.location redirect to login / not-authorized is in flight;
      // hold the navigation instead of rendering the guarded view behind it.
      return new Promise<null>(() => undefined);
    }
  };
